import { createBarrier, isHttpErrorCode } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { chain, disable, enable } from '@leprecoins/effector';
import { createEvent, createStore, sample } from 'effector';
import { condition, not } from 'patronum';
import { createMutation } from './http-client';
import { AuthDto, AuthDtoSchema } from './types';

const refreshMutation = createMutation({
	request: {
		method: 'POST',
		url: `/auth/refresh`,
		credentials: 'include',
		body: {},
	},
	response: {
		contract: zodContract(AuthDtoSchema),
	},
});

const authBarrier = createBarrier({
	activateOn: {
		failure: isHttpErrorCode(401),
	},
	perform: [refreshMutation],
});

const $auth = createStore<AuthDto | null>(null);

const $role = $auth.map((auth) => auth?.role ?? null);

const $token = $auth.map((auth) => auth?.token ?? null);

const $authorized = createStore(false);

const $unauthorized = createStore(false);

const authorized = createEvent<AuthDto>();

const unauthorized = createEvent();

const refresh = createEvent();

sample({
	clock: refreshMutation.finished.success,
	fn: ({ result }) => result,
	target: authorized,
});

chain(authorized, $auth);

enable(authorized, $authorized);

chain(unauthorized, $auth.reinit);

enable(unauthorized, $unauthorized);

disable(unauthorized, $authorized);

disable(authorized, $unauthorized);

chain(refreshMutation.finished.failure, unauthorized);

condition({
	source: refresh,
	if: not($unauthorized),
	then: refreshMutation.start,
	else: unauthorized,
});

export const authModel = {
	authBarrier,
	$auth,
	$role,
	$token,
	$authorized,
	authorized,
	unauthorized,
	refresh,
};
